/** FASE 5C-1 · Jun 2026 — Historial de movimientos bancarios con corrección (solo Admin). */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/context/I18nContext";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);

const TYPES = ["gasto", "reembolso_gasto", "transferencia_no_contabilizar", "deposito_no_contabilizar"];
const CATEGORIES = ["materiales", "publicidad", "software_suscripciones", "servicios", "renta",
  "nomina", "impuestos", "transporte", "comisiones_bancarias", "otros"];
const needsCategory = (t) => t === "gasto" || t === "reembolso_gasto";

export const BankTransactions = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [customCats, setCustomCats] = useState([]);
  const [error, setError] = useState("");
  const [f, setF] = useState({ date_from: "", date_to: "", category: "", movement_type: "", status: "all", q: "" });
  const [batches, setBatches] = useState([]);

  const loadBatches = () =>
    axios.get(`${API}/api/finance/bank/batches`).then((r) => setBatches(r.data.batches)).catch(() => {});

  const load = useCallback(async () => {
    setError("");
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      if (f.status !== "all") params.status = f.status;
      const { data: d } = await axios.get(`${API}/api/finance/bank/transactions`, { params });
      setData(d);
    } catch {
      setError(t("fin_bi_error"));
    }
  }, [f, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/api/finance/bank/categories`).then((r) => setCustomCats(r.data.custom)).catch(() => {});
    loadBatches();
  }, []);

  const toggleBatch = async (b) => {
    const msg = b.voided ? t("fin_bt_react_confirm") : t("fin_bt_void_confirm");
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API}/api/finance/bank/batches/${b.id}/${b.voided ? "reactivate" : "void"}`);
      loadBatches();
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("fin_bi_error"));
    }
  };

  const activeCustom = customCats.filter((c) => c.active);

  const correct = async (tx, patch) => {
    setError("");
    const movement_type = patch.movement_type ?? tx.movement_type;
    let category = patch.category ?? tx.category;
    if (!needsCategory(movement_type)) category = null;
    else if (!category) category = "otros";
    try {
      await axios.patch(`${API}/api/finance/bank/transactions/${tx.id}`, { movement_type, category });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("fin_bi_error"));
    }
  };

  const setFilter = (patch) => setF((prev) => ({ ...prev, ...patch }));

  return (
    <div data-testid="fin-bt" className="space-y-4">
      <div className="label-overline">{t("fin_bt_title")}</div>

      <div className="flex flex-wrap items-end gap-2 text-xs font-mono-tech">
        <input type="date" value={f.date_from} onChange={(e) => setFilter({ date_from: e.target.value })}
          data-testid="fin-bt-filter-start" className="border border-[var(--border)] bg-white px-2 py-1.5" />
        <input type="date" value={f.date_to} onChange={(e) => setFilter({ date_to: e.target.value })}
          data-testid="fin-bt-filter-end" className="border border-[var(--border)] bg-white px-2 py-1.5" />
        <select value={f.movement_type} onChange={(e) => setFilter({ movement_type: e.target.value })}
          data-testid="fin-bt-filter-type" className="border border-[var(--border)] bg-white px-2 py-1.5">
          <option value="">{t("fin_bt_type_all")}</option>
          {TYPES.map((ty) => <option key={ty} value={ty}>{t(`fin_bi_t_${ty}`)}</option>)}
        </select>
        <select value={f.category} onChange={(e) => setFilter({ category: e.target.value })}
          data-testid="fin-bt-filter-cat" className="border border-[var(--border)] bg-white px-2 py-1.5">
          <option value="">{t("fin_bt_cat_all")}</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{t(`fin_bi_c_${c}`)}</option>)}
          {customCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select value={f.status} onChange={(e) => setFilter({ status: e.target.value })}
          data-testid="fin-bt-filter-status" className="border border-[var(--border)] bg-white px-2 py-1.5">
          <option value="all">{t("fin_bt_status_all")}</option>
          <option value="active">{t("fin_bt_status_active")}</option>
          <option value="voided">{t("fin_bt_status_voided")}</option>
        </select>
        <input type="text" value={f.q} placeholder={t("fin_bt_search")}
          onChange={(e) => setFilter({ q: e.target.value })}
          data-testid="fin-bt-filter-q" className="border border-[var(--border)] bg-white px-2 py-1.5 w-48" />
      </div>

      {error && <div data-testid="fin-bt-error" className="border-l-4 border-[#C5221F] bg-[#FDECEA] px-4 py-2 text-sm">{error}</div>}

      {data && data.transactions.length === 0 ? (
        <div data-testid="fin-bt-empty" className="border border-[var(--border)] bg-white p-6 text-sm italic text-[var(--text-secondary)]">
          {t("fin_bt_empty")}
        </div>
      ) : data && (
        <div className="border border-[var(--border)] bg-white overflow-x-auto max-h-[480px] overflow-y-auto">
          <table data-testid="fin-bt-table" className="w-full text-xs min-w-[1000px]">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-black text-left">
                {["fin_bi_col_date", "fin_bi_col_desc", "fin_bi_col_amount", "fin_bi_col_type", "fin_bi_col_category",
                  "fin_bt_col_file", "fin_bt_col_imported_at", "fin_bt_col_imported_by", "fin_bt_col_status"].map((c) => (
                  <th key={c} className="px-2 py-2 text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{t(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.transactions.map((r, i) => (
                <tr key={r.id} data-testid={`fin-bt-row-${i}`} className={`border-b border-[var(--border)] hover:bg-[var(--surface-alt)] ${r.voided ? "opacity-45" : ""}`}>
                  <td className="px-2 py-1.5 font-mono-tech whitespace-nowrap">{r.date}</td>
                  <td className="px-2 py-1.5 max-w-[280px] truncate" title={r.description}>{r.description}</td>
                  <td className={`px-2 py-1.5 font-mono-tech text-right whitespace-nowrap ${r.amount < 0 ? "text-[#C5221F]" : "text-[#0F7B3E]"}`}>{money(r.amount)}</td>
                  <td className="px-2 py-1.5">
                    <select value={r.movement_type} data-testid={`fin-bt-type-${i}`} disabled={r.voided}
                      onChange={(e) => correct(r, { movement_type: e.target.value })}
                      className="border border-[var(--border)] bg-white px-1 py-0.5 text-xs w-full">
                      {TYPES.map((ty) => <option key={ty} value={ty}>{t(`fin_bi_t_${ty}`)}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <select value={r.category || ""} disabled={r.voided || !needsCategory(r.movement_type)}
                      data-testid={`fin-bt-cat-${i}`}
                      onChange={(e) => correct(r, { category: e.target.value })}
                      className="border border-[var(--border)] bg-white px-1 py-0.5 text-xs w-full">
                      {!needsCategory(r.movement_type) && <option value="">—</option>}
                      {CATEGORIES.map((c) => <option key={c} value={c}>{t(`fin_bi_c_${c}`)}</option>)}
                      {activeCustom.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                      {r.category && !CATEGORIES.includes(r.category) && !activeCustom.some((c) => c.name === r.category) && (
                        <option value={r.category}>{r.category}</option>
                      )}
                    </select>
                  </td>
                  <td className="px-2 py-1.5 font-mono-tech text-[10px] whitespace-nowrap">{r.filename}</td>
                  <td className="px-2 py-1.5 font-mono-tech text-[10px] whitespace-nowrap">{(r.imported_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-1.5 text-[10px] whitespace-nowrap">{r.imported_by}</td>
                  <td className="px-2 py-1.5">
                    <span data-testid={`fin-bt-status-${i}`}
                      className={`text-[10px] font-mono-tech font-bold uppercase ${r.voided ? "text-[#C5221F]" : "text-[#0F7B3E]"}`}>
                      {r.voided ? t("fin_bt_status_voided") : t("fin_bt_status_active")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {batches.length > 0 && (
        <div data-testid="fin-bt-batches" className="border border-[var(--border)] bg-white p-4 space-y-2">
          <div className="text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{t("fin_bt_batches_title")}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[700px]">
              <thead>
                <tr className="border-b border-[var(--border)] text-left">
                  {["fin_bt_col_file", "fin_bi_col_date", "fin_bt_col_imported_by", "fin_bi_imported", "fin_bi_skipped", "fin_bt_col_status", ""].map((c, j) => (
                    <th key={j} className="px-2 py-1.5 text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{c ? t(c) : ""}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} data-testid={`fin-bt-batch-${b.id}`} className="border-b border-[var(--border)]">
                    <td className="px-2 py-1.5 font-mono-tech">{b.filename}</td>
                    <td className="px-2 py-1.5 font-mono-tech whitespace-nowrap">{(b.uploaded_at || "").slice(0, 10)}</td>
                    <td className="px-2 py-1.5">{b.imported_by}</td>
                    <td className="px-2 py-1.5 font-mono-tech text-right">{b.imported_count}</td>
                    <td className="px-2 py-1.5 font-mono-tech text-right">{b.skipped_duplicates}</td>
                    <td className={`px-2 py-1.5 text-[10px] font-mono-tech font-bold uppercase ${b.voided ? "text-[#C5221F]" : "text-[#0F7B3E]"}`}>
                      {b.voided ? t("fin_bt_status_voided") : t("fin_bt_status_active")}
                    </td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => toggleBatch(b)} data-testid={`fin-bt-batch-toggle-${b.id}`}
                        className="border border-[var(--border)] px-2 py-0.5 text-[10px] font-mono-tech uppercase hover:border-black">
                        {b.voided ? t("fin_bt_reactivate") : t("fin_bt_void")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
